import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const inputCls =
  "w-full bg-transparent border border-white/10 focus:border-[#C9A050] outline-none px-3 py-2.5 text-sm text-white";

export default function ContactAdmin() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contact").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="overline text-[#8A8A93]">Loading…</div>;

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data: out } = await api.put("/contact", data);
      setData(out);
      toast.success("Contact page saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-10 max-w-3xl">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">Contact</h1>
          <p className="text-sm text-[#8A8A93] mt-2">Edit the public /contact page. Direct contact details (email, phone, WhatsApp, social) are pulled from Settings → Content.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="contact-save" className="overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-3 transition-all disabled:opacity-50">
          {saving ? "Saving…" : "Save"}
        </button>
      </header>

      <SectionCard title="1. Opening line" visKey="opening_visible" data={data} set={set} testid="opening">
        <Field label="Overline">
          <input value={data.opening_overline || ""} onChange={(e) => set("opening_overline", e.target.value)} className={inputCls} placeholder="Contact" />
        </Field>
        <Field label="Headline">
          <textarea rows={2} value={data.opening_line || ""} onChange={(e) => set("opening_line", e.target.value)} className={inputCls} placeholder="Tell us what you're making." />
        </Field>
        <Field label="Italic-gold accent word" hint="Leave empty to auto-style the last word.">
          <input value={data.opening_accent || ""} onChange={(e) => set("opening_accent", e.target.value)} className={inputCls} placeholder="making" />
        </Field>
      </SectionCard>

      <SectionCard title="2. Direct contact" visKey="direct_visible" data={data} set={set} testid="direct">
        <Field label="Section label">
          <input value={data.direct_label || ""} onChange={(e) => set("direct_label", e.target.value)} className={inputCls} placeholder="Direct" />
        </Field>
        <p className="text-xs text-[#8A8A93]">The email, phone, and WhatsApp numbers shown here come from <strong>Settings → Content</strong>. Edit those there.</p>
      </SectionCard>

      <SectionCard title="3. Studio / location" visKey="location_visible" data={data} set={set} testid="location">
        <Field label="Section label">
          <input value={data.location_label || ""} onChange={(e) => set("location_label", e.target.value)} className={inputCls} placeholder="Studio" />
        </Field>
        <Field label="Address (multi-line)">
          <textarea rows={3} value={data.location_body || ""} onChange={(e) => set("location_body", e.target.value)} className={inputCls} placeholder="Coimbatore, India&#10;By appointment" />
        </Field>
        <Field
          label="Google Maps embed URL (optional)"
          hint="On Google Maps: search your address → Share → Embed a map → copy ONLY the src URL from the iframe (the part starting with https://www.google.com/maps/embed?pb=…). The map will appear under the address on /contact."
        >
          <textarea
            rows={2}
            value={data.location_map_embed || ""}
            onChange={(e) => set("location_map_embed", e.target.value)}
            data-testid="contact-location-map"
            className={inputCls}
            placeholder="https://www.google.com/maps/embed?pb=…"
          />
        </Field>
      </SectionCard>

      <SectionCard title="4. Social / Elsewhere" visKey="social_visible" data={data} set={set} testid="social">
        <Field label="Section label">
          <input value={data.social_label || ""} onChange={(e) => set("social_label", e.target.value)} className={inputCls} placeholder="Elsewhere" />
        </Field>
        <p className="text-xs text-[#8A8A93]">Instagram / LinkedIn / Twitter URLs come from <strong>Settings → Content</strong>.</p>
      </SectionCard>

      <SectionCard title="5. The form" visKey="form_visible" data={data} set={set} testid="form">
        <Field label="Form section label (above the form)">
          <input value={data.form_label || ""} onChange={(e) => set("form_label", e.target.value)} className={inputCls} placeholder="Tell us about your project" />
        </Field>
        <Field label="Success message body">
          <textarea rows={2} value={data.success_body || ""} onChange={(e) => set("success_body", e.target.value)} className={inputCls} placeholder="We'll get back to you within 48 hours." />
        </Field>
        <p className="text-xs text-[#8A8A93]">Form submissions go to your inbox via Resend (configured in backend env).</p>
      </SectionCard>

      <SectionCard title="6. Closing line" visKey="closing_visible" data={data} set={set} testid="closing">
        <Field label="Closing line">
          <textarea rows={2} value={data.closing_body || ""} onChange={(e) => set("closing_body", e.target.value)} className={inputCls} placeholder="Quiet, careful work — built to outlast the trends." />
        </Field>
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, visKey, data, set, testid, children }) {
  const visible = data[visKey] !== false;
  return (
    <section className="border border-white/5 p-6 md:p-7" data-testid={`contact-section-${testid}`}>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl tracking-tight">{title}</h2>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={visible} onChange={(e) => set(visKey, e.target.checked)} data-testid={`contact-section-${testid}-visible`} className="w-4 h-4 accent-[#C9A050]" />
          <span className="overline text-xs">Show on /contact</span>
        </label>
      </div>
      <div className={visible ? "" : "opacity-60"}>
        <div className={`space-y-5 ${visible ? "" : "[&_input:not([type=checkbox])]:opacity-50 [&_textarea]:opacity-50"}`}>{children}</div>
      </div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="overline block mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#8A8A93] mt-2">{hint}</p>}
    </div>
  );
}
